/*
 * audit-trail browser: response shaping and error routing. Pure; the tab renders what these
 * return.
 */
import { EM_DASH, api_error, fmt, is_permission_error, month_label } from '../format';
import { with_ist } from '../shared/ist';

// "pass_keep_unused" -> "Pass keep unused": sentence case, the code itself stays in the title.
export const action_label = (action) => {
  const words = String(action || '')
    .split('_')
    .filter(Boolean)
    .join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : EM_DASH;
};

// Free-text fields (engine notes, reasons): clamped in the list with Show more, never amounts.
const LONG_TEXT_FIELD = /(^|\.)(notes?|reason|comment|description)$/;
export const is_long_text_field = (field) => LONG_TEXT_FIELD.test(String(field || ''));

// A value as Ops should read it: missing -> em dash, blank text says so, objects/arrays as JSON;
// a *_paise amount reads as ₹ and a list of text lines as one line each.
export const audit_value_text = (v, field = '') => {
  if (v === null || v === undefined) return EM_DASH;
  if (v === '') return '(blank)';
  if (/_paise$/.test(field) && typeof v === 'number') return fmt(v / 100, 2);
  if (Array.isArray(v) && v.length && v.every((x) => typeof x === 'string')) return v.join('\n');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const change_view = (c) => {
  const field = String((c && c.field) || '');
  return {
    field,
    old_text: audit_value_text(c && c.old, field),
    new_text: audit_value_text(c && c.new, field)
  };
};

const actor_text = (actor) => {
  if (!actor || typeof actor !== 'object') return EM_DASH;
  return actor.name || actor.id || EM_DASH;
};

// The When cell: the date, then the time and zone together so 'IST' never sits alone on a line.
export const when_parts = (when) => {
  const [day, ...rest] = String(when).split(' ');
  return rest.length ? [day, rest.join(' ')] : [day];
};

export const audit_row_view = (r) => ({
  id: r.id,
  when: with_ist(r.created_at_ist) || EM_DASH,
  entity_label: r.entity_label || r.entity_type || EM_DASH,
  entity_id: r.entity_id || '',
  action: action_label(r.action),
  actor: actor_text(r.actor),
  teacher_name: r.teacher_name || '',
  teacher_uuid: r.teacher_uuid || '',
  // NULL pay_month rows (profile changes, jobs) are listed under the IST month they happened in.
  month: r.pay_month ? month_label(r.pay_month) : EM_DASH,
  reason: r.reason || '',
  changes: Array.isArray(r.changes) ? r.changes.map(change_view) : []
});

export const audit_page_view = (data) => {
  const d = data || {};
  const next_before = Number(d.next_before_id);
  const next_off = Number(d.next_offset);
  return {
    rows: (Array.isArray(d.rows) ? d.rows : []).map(audit_row_view),
    // The default (keyset) order pages by next_before_id; a column sort pages by next_offset instead
    // (reports.AuditListResponse) — the two are never both set.
    next_before_id: Number.isFinite(next_before) && next_before > 0 ? next_before : null,
    next_offset: Number.isFinite(next_off) && next_off > 0 ? next_off : null
  };
};

// "Load older" appends; an id already listed (a double press) is not listed twice.
export const append_page = (rows, page) => {
  const seen = new Set(rows.map((r) => r.id));
  return rows.concat(page.rows.filter((r) => !seen.has(r.id)));
};

const facet_options = (list, to_option) => (Array.isArray(list) ? list : []).map(to_option);

export const audit_facets_view = (data) => {
  const d = data || {};
  return {
    entity_types: facet_options(d.entity_types, (f) => ({
      value: f.value,
      label: `${f.label || f.value} (${f.count})`
    })),
    actions: facet_options(d.actions, (f) => ({
      value: f.value,
      label: `${action_label(f.label || f.value)} (${f.count})`
    })),
    actors: facet_options(d.actors, (a) => ({ value: a.id, label: a.name || a.id }))
  };
};

// The name for a deep-linked teacher, known only once a row of theirs comes back.
export const teacher_name_from_rows = (rows, teacher_uuid) => {
  const hit = rows.find((r) => r.teacher_uuid === teacher_uuid && r.teacher_name);
  return hit ? hit.teacher_name : '';
};

/*
 * Every code an audit read can return (reports_enums.json error_codes minus the report-only ones).
 */
export const AUDIT_ERROR_TEXT = {
  INVALID_REQUEST: 'The filters could not be read. Reset them and try again.',
  INVALID_PAY_MONTH: 'The month must look like 2026-09.',
  AUDIT_FILTER_REQUIRED: 'Choose a month or a teacher to see the audit trail.',
  INVALID_PAGINATION: 'Could not load older entries. Reload the list and try again.',
  TEACHER_NOT_FOUND: 'No teacher matches this link. Pick the teacher again.',
  BAD_SORT: 'Could not sort the audit trail that way. Reload the list and try again.',
  LOOKUP_FAILED: 'Something went wrong on the server. Try again.'
};
export const AUDIT_PERMISSION_CODES = ['FORBIDDEN'];

export const audit_error_view = (e, fallback) => {
  const { code, message } = api_error(e);
  if (is_permission_error(e) || AUDIT_PERMISSION_CODES.indexOf(code) !== -1) {
    return { kind: 'permission', code };
  }
  return { kind: 'inline', code, text: message || AUDIT_ERROR_TEXT[code] || fallback };
};
